import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import tinycolor from 'tinycolor2';
import { userAPI } from '../UserAPI';
import { useCustomerTheme } from '../Theme';
import { useModalContext } from './ModalProvider';
import { isViewableDocument, useDocumentViewer } from '../documentViewer/DocumentViewerContext';

import { BookOpen, Download, ExternalLink, Eye, FileText } from 'lucide-react';

import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

export interface ProcessInstanceDocumentReferencesProps {
    /** From process instance API `documentReferences`. */
    documentReferences: any[];
    /** Optional: merge in `conversation.contextDocuments` when present (deduped by `_id`). */
    conversationContextDocuments?: any[] | null;
    customerId: string;
    /** Linked conversation id (enables context-document download fallback). */
    conversationId?: string | null;
    /** Chips (default, as in the chat) or a table with one row per document. */
    layout?: 'chips' | 'table';
}

/** "PDF", "DOCX" — from the name, which the upload path trusts over the MIME type; else the MIME subtype. */
function fileTypeLabel(doc: any): string {
    const extension = String(doc?.filename ?? '').match(/\.([a-z0-9]{1,8})$/i)?.[1];
    if (extension) return extension.toUpperCase();
    const subtype = String(doc?.mimeType ?? '').split('/')[1];
    return subtype ? subtype.toUpperCase() : '—';
}

function doDownloadBlob(blob: Blob, downloadFilename: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = downloadFilename;
    a.click();
    URL.revokeObjectURL(url);
}

/**
 * Read-only context documents: same affordances as ChatExchange (tooltip, download, open, markdown)
 * using `fileId` and optional `conversationId` + document `_id` fallbacks.
 */
export const ProcessInstanceDocumentReferences: React.FC<ProcessInstanceDocumentReferencesProps> = ({
    documentReferences,
    conversationContextDocuments,
    customerId,
    conversationId,
    layout = 'chips',
}) => {
    const { t } = useTranslation();
    const { theme } = useCustomerTheme();
    const { showMessageDialog } = useModalContext() as {
        showMessageDialog?: (title: string, message: string, onClose?: () => void | Promise<void>) => void;
    };
    const [documentMarkdownDialog, setDocumentMarkdownDialog] = useState<{ doc: any } | null>(null);
    const documentViewer = useDocumentViewer();

    const handleDocumentDownload = useCallback(
        async (doc: any) => {
            const fileId = (doc as any).fileId;
            const filename = (doc as any).filename || 'document';

            if (fileId) {
                try {
                    const { blob, filename: fn } = await userAPI.getDocumentByFileId(
                        customerId,
                        fileId,
                        'download',
                        filename,
                    );
                    doDownloadBlob(blob, fn);
                    return;
                } catch (err) {
                    console.error('Failed to download original via fileId:', err);
                    try {
                        const { blob, filename: fn } = await userAPI.getDocumentByFileId(
                            customerId,
                            fileId,
                            'markdown',
                            filename,
                        );
                        doDownloadBlob(blob, fn);
                        return;
                    } catch (markdownErr) {
                        console.error('Failed to download markdown via fileId:', markdownErr);
                    }
                }
            }

            const documentId = (doc as any)._id;
            if (conversationId && documentId) {
                try {
                    const { blob, filename: fn } = await userAPI.getContextDocumentDownload(
                        customerId,
                        conversationId,
                        documentId,
                        filename,
                    );
                    doDownloadBlob(blob, fn);
                    return;
                } catch (err) {
                    console.error('Failed to download via context-documents:', err);
                }
            }

            const content = (doc as any).markdown || (doc as any).content || '';
            const ext = ((doc as any).filename || '').toLowerCase();
            const isTextOriginal = ext.endsWith('.md') || ext.endsWith('.txt') || ext.endsWith('.html');
            if (content && isTextOriginal) {
                const blob = new Blob([content], { type: 'text/markdown' });
                const downloadFilename =
                    ((doc as any).filename || 'document').replace(/\.[^.]+$/, '') + (ext.endsWith('.md') ? '' : '.md');
                doDownloadBlob(blob, downloadFilename);
            } else if (!content) {
                showMessageDialog?.(t('global.generalError'), t('chatExchange.documentDownloadFailed', { filename }));
            } else {
                const blob = new Blob([content], { type: 'text/markdown' });
                const downloadFilename = ((doc as any).filename || 'document').replace(/\.[^.]+$/, '') + '.md';
                doDownloadBlob(blob, downloadFilename);
            }
        },
        [customerId, conversationId, showMessageDialog, t],
    );

    const documents = useMemo(() => {
        const refs = documentReferences ?? [];
        const conv = conversationContextDocuments ?? [];
        const seen = new Set<string>();
        const out: any[] = [];
        for (const d of refs) {
            const id = d?._id != null ? String(d._id) : null;
            if (id) seen.add(id);
            out.push(d);
        }
        for (const d of conv) {
            const id = d?._id != null ? String(d._id) : null;
            if (id && !seen.has(id)) {
                seen.add(id);
                out.push(d);
            }
        }
        return out;
    }, [documentReferences, conversationContextDocuments]);

    const handleDocumentOpen = useCallback(
        async (doc: any) => {
            const fileId = (doc as any).fileId;
            if (fileId) {
                try {
                    const { blob } = await userAPI.getDocumentByFileId(
                        customerId,
                        fileId,
                        'markdown',
                        (doc as any).filename,
                    );
                    const url = URL.createObjectURL(blob);
                    window.open(url, '_blank', 'noopener,noreferrer');
                    setTimeout(() => URL.revokeObjectURL(url), 1000);
                    return;
                } catch (err) {
                    console.error('Failed to open markdown via fileId:', err);
                }
            }
            const content = (doc as any).markdown || (doc as any).content || '';
            if (!content) return;
            const blob = new Blob([content], { type: 'text/markdown' });
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank', 'noopener,noreferrer');
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        },
        [customerId],
    );

    const handleDocumentMarkdown = useCallback(
        async (doc: any) => {
            const fileId = (doc as any).fileId;
            let docToShow = doc;
            if (fileId && !(doc as any).markdown && !(doc as any).content) {
                try {
                    const { blob } = await userAPI.getDocumentByFileId(
                        customerId,
                        fileId,
                        'markdown',
                        (doc as any).filename,
                    );
                    const content = await blob.text();
                    docToShow = { ...doc, markdown: content };
                } catch (err) {
                    console.error('Failed to fetch markdown via fileId:', err);
                }
            }
            setDocumentMarkdownDialog({ doc: docToShow });
        },
        [customerId],
    );

    if (!documents?.length) {
        return (
            <div className="text-sm text-muted-foreground">
                {t('processPanel.processDocumentReferencesEmpty')}
            </div>
        );
    }

    const accentColor = theme?.primaryDark || theme?.primary || '#0078d4';
    const chipBackground = tinycolor(theme?.secondaryLighter ?? '#deecf9').setAlpha(0.15).toRgbString();

    const markdownDialog = documentMarkdownDialog && (
        <Dialog
            open
            onOpenChange={(open) => {
                if (!open) {
                    setDocumentMarkdownDialog(null);
                }
            }}
        >
            <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-[800px]">
                <DialogHeader>
                    <DialogTitle>
                        {t('chatExchange.documentMarkdownTitle', {
                            filename: documentMarkdownDialog.doc?.filename || '',
                        })}
                    </DialogTitle>
                </DialogHeader>
                <div className="markdown max-h-[70vh] overflow-auto p-4">
                    <Markdown rehypePlugins={[rehypeRaw] as any} remarkPlugins={[remarkGfm]}>
                        {(documentMarkdownDialog.doc?.markdown || documentMarkdownDialog.doc?.content || '') as string}
                    </Markdown>
                </div>
                <DialogFooter>
                    <Button type="button" onClick={() => setDocumentMarkdownDialog(null)}>{t('global.close')}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );

    if (layout === 'table') {
        return (
            <>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{t('processPanel.processDocumentReferencesColumnFile')}</TableHead>
                            <TableHead className="w-24">{t('processPanel.processDocumentReferencesColumnType')}</TableHead>
                            <TableHead className="w-0 whitespace-nowrap text-right">
                                <span className="sr-only">{t('processPanel.processDocumentReferencesColumnActions')}</span>
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {documents.map((doc: any, idx: number) => {
                            const hasDocumentData = !!(doc.markdown || doc.content || doc.fileId);
                            // The same three actions the chip's menu offers, as buttons in the row — plus, for a
                            // PDF with a file id, the document viewer (the original, not its markdown).
                            const canView = !!documentViewer && !!doc.fileId && isViewableDocument(doc.filename, doc.mimeType);
                            const actions = [
                                ...(canView
                                    ? [{
                                        icon: BookOpen,
                                        label: t('documentViewer.view'),
                                        run: (d: any) => documentViewer!.openDocument({ fileId: String(d.fileId), filename: d.filename, webUrl: d.webUrl }),
                                    }]
                                    : []),
                                { icon: Download, label: t('chatExchange.documentDownload'), run: handleDocumentDownload },
                                { icon: Eye, label: t('chatExchange.documentMarkdownShow'), run: handleDocumentMarkdown },
                                { icon: ExternalLink, label: t('chatExchange.documentMarkdownOpen'), run: handleDocumentOpen },
                            ];
                            return (
                                <TableRow key={doc._id ?? idx}>
                                    <TableCell className="py-1.5">
                                        {/* The summary is what the chip's tooltip showed; here it is the row's title. */}
                                        <div className="flex min-w-0 items-center gap-1.5" title={doc.summary || undefined}>
                                            <FileText aria-hidden className="h-3.5 w-3.5 shrink-0" style={{ color: accentColor }} />
                                            <span className="min-w-0 break-words">{doc.filename}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell className="py-1.5 text-xs text-muted-foreground">{fileTypeLabel(doc)}</TableCell>
                                    <TableCell className="whitespace-nowrap py-1 text-right">
                                        {hasDocumentData &&
                                            actions.map(({ icon: Icon, label, run }) => (
                                                <Button
                                                    key={label}
                                                    type="button"
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-7 w-7"
                                                    title={label}
                                                    aria-label={`${label}: ${doc.filename}`}
                                                    onClick={() => run(doc)}
                                                >
                                                    <Icon aria-hidden className="h-3.5 w-3.5" />
                                                </Button>
                                            ))}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
                {markdownDialog}
            </>
        );
    }

    return (
        <TooltipProvider>
            <div className="flex flex-wrap items-start gap-2">
                {documents.map((doc: any, idx: number) => {
                    const hasDocumentData = !!((doc as any).markdown || (doc as any).content || (doc as any).fileId);
                    const canOpenDocumentMenu = hasDocumentData;

                    const tooltipContent = (
                        <div>
                            <div className="mb-2 font-bold">
                                {(doc as any).filename}
                            </div>
                            {(doc as any).mimeType && (
                                <div className="mb-1 text-xs text-primary-foreground/80">
                                    {(doc as any).mimeType}
                                </div>
                            )}
                            {(doc as any).summary && <div className="mt-1">{(doc as any).summary}</div>}
                            {(doc as any).fileId && (
                                <div className="mt-2 break-all font-mono text-xs text-primary-foreground/70">
                                    fileId: {(doc as any).fileId}
                                </div>
                            )}
                            {(doc as any)._id && (
                                <div className="mt-1 break-all font-mono text-xs text-primary-foreground/70">
                                    id: {(doc as any)._id}
                                </div>
                            )}
                            {(doc as any).path && (
                                <div className="mt-1 break-all text-xs text-primary-foreground/70">
                                    {(doc as any).path}
                                </div>
                            )}
                        </div>
                    );

                    const chipClassName =
                        'flex min-w-0 max-w-[280px] items-center rounded-md px-2 py-0.5 text-left';
                    const chipInner = (
                        <>
                            <FileText aria-hidden className="mr-1.5 h-3.5 w-3.5 shrink-0" style={{ color: accentColor }} />
                            <span className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-xs">
                                {(doc as any).filename}
                            </span>
                        </>
                    );

                    if (!canOpenDocumentMenu) {
                        return (
                            <Tooltip key={(doc as any)._id ?? idx}>
                                <TooltipTrigger asChild>
                                    <div className={chipClassName} style={{ background: chipBackground }}>
                                        {chipInner}
                                    </div>
                                </TooltipTrigger>
                                <TooltipContent sideOffset={8}>{tooltipContent}</TooltipContent>
                            </Tooltip>
                        );
                    }

                    return (
                        <DropdownMenu key={(doc as any)._id ?? idx}>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <DropdownMenuTrigger asChild>
                                        <button
                                            type="button"
                                            className={`${chipClassName} cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring`}
                                            style={{ background: chipBackground }}
                                        >
                                            {chipInner}
                                        </button>
                                    </DropdownMenuTrigger>
                                </TooltipTrigger>
                                <TooltipContent sideOffset={8}>{tooltipContent}</TooltipContent>
                            </Tooltip>
                            <DropdownMenuContent align="start" className="min-w-[160px]">
                                <DropdownMenuItem onSelect={() => handleDocumentDownload(doc)}>
                                    {t('chatExchange.documentDownload')}
                                </DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => handleDocumentOpen(doc)}>
                                    {t('chatExchange.documentMarkdownOpen')}
                                </DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => handleDocumentMarkdown(doc)}>
                                    {t('chatExchange.documentMarkdownShow')}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    );
                })}
            </div>

            {markdownDialog}
        </TooltipProvider>
    );
};
